// Hand-written transport that backs the generated typed REST client.
//
// The generated resource classes (e.g. `class CruiseApi`) call
// `ApiClient.call(key, args)` with a `"METHOD /api/path/:param"` endpoint key and a
// typed `{ params, query?, body? }` bag. This adapter turns that back into logbook's
// stringly-typed `Client.call(method, action, props?)`:
//   - splits the key into HTTP method + path template,
//   - substitutes `:params` from `args.params`,
//   - drops the leading `/api/` that `Client.call` re-adds, and
//   - forwards `query` (GET) or `body` (otherwise) as the props bag.
// It stays a thin delegate so the pre-migration `Client.call` wire contract — and the
// tests pinned to it — hold unchanged after a resource moves onto the typed client.
//
// It also implements `.url(key, args)`: the same `:param` substitution, but returning
// the route's own path (keeping its `/api/…` API prefix or bare `/ships/…/download`
// file-route prefix, plus any query) so consumers can embed a route URL — e.g. an
// attachment `<img src>` — without issuing a request.
import { Client, ApiProps } from "./Client"
import type { ApiClient, ApiTarget } from "../Types/Generated/LogbookApi/ApiRuntime"
import {
  createGeneratedApiClient,
  GeneratedApiArgs,
  resolveGeneratedApiCall,
} from "app-base/client/routes"

// A multipart upload route carries its file in the request body (e.g. the generated
// `body: { attachment: File }`); every other body value is a plain scalar prop.
type ApiBodyValue = string | number | boolean | null | undefined | File;

interface ApiCallArgs extends GeneratedApiArgs {
  params?: Record<string, string | number>;
  query?: ApiProps;
  body?: Record<string, ApiBodyValue>;
}

export type { ApiCallArgs }

// `target` is the caller's execution context ("server" on a cloud session, "app"
// otherwise). The generated `.call()` asserts it against each route's allowed
// `targets` before hitting the transport, so a server client calling an app-only
// route throws `ApiTargetError` instead of issuing a request. Constructed once in
// `App.tsx` from `app.cloudSession()` so every view shares the correct target.
export function createApiClient(target: ApiTarget, session?: string): ApiClient {
  return createGeneratedApiClient({
    target,
    call<R>(method: string, action: string, props?: ApiProps, file?: File): Promise<R> {
      const client = new Client(session);
      if (file !== undefined) {
        return client.call<R>(method, action, props ?? {}, file);
      }
      return props === undefined
        ? client.call<R>(method, action)
        : client.call<R>(method, action, props);
    },
    requestPath: (path) => path.startsWith("/api/") ? path.slice("/api/".length) : path.replace(/^\//, ""),
    requestBody: (body) => {
      const { props, file } = splitFile(body as Record<string, ApiBodyValue> | undefined);
      return { request: props, file };
    },
  }) as ApiClient;
}

export function resolveCall(
  key: string,
  args: ApiCallArgs,
): { method: string; action: string; props?: ApiProps; file?: File } {
  const { method, path, request } = resolveGeneratedApiCall(key, args);
  const action = path.startsWith("/api/") ? path.slice("/api/".length) : path.replace(/^\//, "");
  if (method === "GET") {
    return { method, action, props: request as ApiProps | undefined };
  }
  // A multipart route folds its file into `body`; split it out so the transport can
  // send it as `FormData` (4th arg) with the remaining scalar fields as props.
  const { props, file } = splitFile(request as Record<string, ApiBodyValue> | undefined);
  return { method, action, props, file };
}

function splitFile(
  body: Record<string, ApiBodyValue> | undefined,
): { props?: ApiProps; file?: File } {
  if (body === undefined) {
    return {};
  }
  const props: ApiProps = {};
  let file: File | undefined;
  for (const [name, value] of Object.entries(body)) {
    if (value instanceof File) {
      file = value;
    } else {
      props[name] = value;
    }
  }
  return { props, file };
}
